import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "../components/ui/dialog";
import { Search, Plus, Trash2, Pencil, Users, Shield, ChevronDown, Star, UserCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';
import CrewStaffModal from '@/components/CrewStaffModal';
import TeamStaffModal from '@/components/TeamStaffModal';
import IncidentOriginChip from '@/components/IncidentOriginChip';
import { loadAssignUsers } from '@/components/AssignPersonnelModal';

// Teams & Crews — a simple reference collection: Team → Crews → Members.
// Teams/crews created here (or from a live incident's Participants widget) are
// the same records used when assigning crews during real incidents and drills.
// Crew membership uses the same mechanism as the incident-side Crew Staff
// modal: users whose profile points at the crew + members stored on the crew.
const TeamsAndCrews = () => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState({}); // teamId -> bool

  // Team create/edit dialog
  const [teamDialog, setTeamDialog] = useState(null); // { id?, team_name, description }
  const [savingTeam, setSavingTeam] = useState(false);

  // Inline "add crew" drafts per team, and inline crew rename
  const [crewDraft, setCrewDraft] = useState({}); // teamId -> name being typed
  const [editingCrew, setEditingCrew] = useState(null); // { teamId, crewId, name }

  // Members modal (same one used from the incident Participants widget)
  const [crewStaffTarget, setCrewStaffTarget] = useState(null);
  const [teamStaffTarget, setTeamStaffTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [teamsRes, { raw }] = await Promise.all([
        CommanServices.getTeamsData(),
        loadAssignUsers(),
      ]);
      setTeams(teamsRes?.status && Array.isArray(teamsRes.data) ? teamsRes.data : []);
      setUsers(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.error('Error loading teams:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredTeams = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) =>
      String(t.team_name || '').toLowerCase().includes(q) ||
      String(t.description || '').toLowerCase().includes(q) ||
      (t.crews || []).some((c) => String(c.crew_name || '').toLowerCase().includes(q))
    );
  }, [teams, searchTerm]);

  // Member count per crew: profile members (users.crew_id) + members recorded
  // on the crew itself (escalation_rules entries hold the user references).
  const crewMemberCount = useCallback((crew) => {
    const seen = new Set();
    users.forEach((u) => {
      if (u.is_active === false) return;
      if (String(u.crew_id || '') === String(crew.id)) seen.add(String(u.id));
    });
    (crew.escalation_rules || []).forEach((rule) => {
      if (rule?.is_active === false) return;
      const targets = Array.isArray(rule.escalated_to) ? rule.escalated_to : [rule.escalated_to];
      targets.filter(Boolean).forEach((t) => {
        const r = String(t).toLowerCase();
        const u = users.find((x) =>
          String(x.id).toLowerCase() === r ||
          String(x.email || '').toLowerCase() === r ||
          String(x.user_account || '').toLowerCase() === r
        );
        if (u) seen.add(String(u.id));
      });
    });
    return seen.size;
  }, [users]);

  /* ------------------------------- Teams ------------------------------- */

  const saveTeam = async () => {
    const name = (teamDialog?.team_name || '').trim();
    if (!name) { toast.error('Team name is required'); return; }
    setSavingTeam(true);
    try {
      const res = teamDialog.id
        ? await CommanServices.updateteams(teamDialog.id, { team_name: name, description: teamDialog.description || '' })
        : await CommanServices.addteams({ team_name: name, description: teamDialog.description || '', trigger_conditions: [], crews: [] });
      if (res?.status === false) { toast.error(res?.message || 'Failed to save team'); return; }
      toast.success(teamDialog.id ? 'Team updated' : 'Team created');
      setTeamDialog(null);
      await load();
    } finally {
      setSavingTeam(false);
    }
  };

  const removeTeam = async (team) => {
    const crewCount = (team.crews || []).length;
    if (!window.confirm(`Delete team "${team.team_name}"${crewCount ? ` and its ${crewCount} crew(s)` : ''}?`)) return;
    const res = await CommanServices.deleteTeams(team.id);
    if (res?.status === false) { toast.error(res?.message || 'Failed to delete team'); return; }
    toast.success('Team deleted');
    await load();
  };

  /* ------------------------------- Crews ------------------------------- */

  const addCrew = async (team) => {
    const name = (crewDraft[team.id] || '').trim();
    if (!name) { toast.error('Crew name is required'); return; }
    const res = await CommanServices.addTeamCrew(team.id, {
      crew_name: name,
      is_primary_crew: (team.crews || []).length === 0, // first crew defaults to primary
      escalation_rules: [],
      description: '',
      is_active: true,
    });
    if (res?.status === false) { toast.error(res?.message || 'Failed to add crew'); return; }
    toast.success(`Crew "${name}" added`);
    setCrewDraft((prev) => ({ ...prev, [team.id]: '' }));
    await load();
  };

  const renameCrew = async () => {
    if (!editingCrew) return;
    const name = (editingCrew.name || '').trim();
    if (!name) { toast.error('Crew name is required'); return; }
    const res = await CommanServices.updateTeamCrew(editingCrew.teamId, editingCrew.crewId, { crew_name: name });
    if (res?.status === false) { toast.error(res?.message || 'Failed to rename crew'); return; }
    toast.success('Crew renamed');
    setEditingCrew(null);
    await load();
  };

  const togglePrimary = async (team, crew) => {
    const res = await CommanServices.updateTeamCrew(team.id, crew.id, { is_primary_crew: !crew.is_primary_crew });
    if (res?.status === false) { toast.error(res?.message || 'Failed to update crew'); return; }
    await load();
  };

  const removeCrew = async (team, crew) => {
    if (!window.confirm(`Delete crew "${crew.crew_name}" from "${team.team_name}"?`)) return;
    const res = await CommanServices.deleteTeamCrew(team.id, crew.id);
    if (res?.status === false) { toast.error(res?.message || 'Failed to delete crew'); return; }
    toast.success('Crew deleted');
    await load();
  };

  /* ------------------------------- Render ------------------------------ */

  return (
    <div className="h-screen bg-gray-50 flex flex-1 overflow-hidden">
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Teams &amp; Crews</h2>
          <p className="text-sm text-gray-600 mb-4">
            Reference collection of response teams. Each team holds crews, and each crew holds members.
            Teams and crews listed here — including ones created from a live incident — are what you assign
            when staffing real incidents and drills.
          </p>

          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                data-testid="teams-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search teams and crews…"
                className="w-full text-sm border border-gray-300 rounded-lg bg-white pl-9 pr-3 py-2"
              />
            </div>
            <button
              data-testid="add-team-btn"
              onClick={() => setTeamDialog({ team_name: '', description: '' })}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold px-3.5 py-2.5 hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add Team
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-sm text-gray-400" data-testid="teams-loading">Loading teams…</div>
        ) : filteredTeams.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-12" data-testid="teams-empty">
            <Users className="h-9 w-9 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 font-medium">
              {searchTerm ? 'No teams match your search' : 'No teams defined yet'}
            </p>
            {!searchTerm && (
              <p className="text-xs text-gray-400 mt-1">
                Add a team here, or create one from a live incident's Participants widget.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTeams.map((team) => {
              const crews = team.crews || [];
              const isOpen = expanded[team.id] !== false; // open by default
              return (
                <div key={team.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid={`team-card-${team.id}`}>
                  {/* Team header */}
                  <div className="flex items-center gap-3 px-5 py-3.5 border-b bg-slate-50/60">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white flex-shrink-0">
                      <Users className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-gray-900 leading-tight truncate">{team.team_name}</h3>
                        <IncidentOriginChip description={team.description} />
                        {team.is_active === false && (
                          <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">Inactive</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">
                        {crews.length} crew{crews.length === 1 ? '' : 's'}
                        {team.description ? ` · ${team.description}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`team-members-${team.id}`}
                        onClick={() => setTeamStaffTarget(team)}
                        className="flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                        title="View and manage team members and leaders"
                      >
                        <UserCircle2 className="h-3.5 w-3.5" /> Members
                      </button>
                      <button
                        data-testid={`edit-team-${team.id}`}
                        onClick={() => setTeamDialog({ id: team.id, team_name: team.team_name || '', description: team.description || '' })}
                        className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                        title="Edit team"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`delete-team-${team.id}`}
                        onClick={() => removeTeam(team)}
                        className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-rose-600"
                        title="Delete team"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`toggle-team-${team.id}`}
                        onClick={() => setExpanded((prev) => ({ ...prev, [team.id]: !isOpen }))}
                        className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100"
                        title={isOpen ? 'Collapse' : 'Expand'}
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>

                  {/* Crews */}
                  {isOpen && (
                    <div className="px-5 py-3 space-y-2">
                      {crews.length === 0 && (
                        <p className="text-xs text-gray-400 py-1">No crews yet — add the first crew below.</p>
                      )}
                      {crews.map((crew) => {
                        const memberCount = crewMemberCount(crew);
                        const isEditing = editingCrew && editingCrew.crewId === crew.id;
                        return (
                          <div key={crew.id} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-slate-50/50 px-3 py-2.5" data-testid={`crew-row-${crew.id}`}>
                            <Shield className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                            {isEditing ? (
                              <div className="flex-1 flex items-center gap-2">
                                <Input
                                  autoFocus
                                  value={editingCrew.name}
                                  onChange={(e) => setEditingCrew((prev) => ({ ...prev, name: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === 'Enter') renameCrew(); if (e.key === 'Escape') setEditingCrew(null); }}
                                  className="h-8 text-sm bg-white"
                                  data-testid={`crew-rename-input-${crew.id}`}
                                />
                                <Button size="sm" className="h-8 text-xs" onClick={renameCrew}>Save</Button>
                                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setEditingCrew(null)}>Cancel</Button>
                              </div>
                            ) : (
                              <>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-sm font-semibold text-gray-800 truncate">{crew.crew_name}</span>
                                    <IncidentOriginChip description={crew.description} />
                                    {crew.is_primary_crew && (
                                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                                        <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" /> Primary
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-gray-500">
                                    {memberCount} member{memberCount === 1 ? '' : 's'}
                                  </p>
                                </div>
                                <button
                                  data-testid={`crew-members-${crew.id}`}
                                  onClick={() => setCrewStaffTarget({ id: crew.id, crew_name: crew.crew_name, team_id: team.id, team_name: team.team_name })}
                                  className="flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-1.5 hover:bg-indigo-100"
                                >
                                  <UserCircle2 className="h-3.5 w-3.5" /> Members
                                </button>
                                <button
                                  data-testid={`crew-primary-${crew.id}`}
                                  onClick={() => togglePrimary(team, crew)}
                                  className={`p-1.5 rounded-md ${crew.is_primary_crew ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-300 hover:bg-gray-100 hover:text-amber-500'}`}
                                  title={crew.is_primary_crew ? 'Unset primary crew' : 'Set as primary crew'}
                                >
                                  <Star className={`h-3.5 w-3.5 ${crew.is_primary_crew ? 'fill-amber-400' : ''}`} />
                                </button>
                                <button
                                  data-testid={`crew-rename-${crew.id}`}
                                  onClick={() => setEditingCrew({ teamId: team.id, crewId: crew.id, name: crew.crew_name || '' })}
                                  className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                                  title="Rename crew"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  data-testid={`crew-delete-${crew.id}`}
                                  onClick={() => removeCrew(team, crew)}
                                  className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-rose-600"
                                  title="Delete crew"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}

                      {/* Add crew inline */}
                      <div className="flex items-center gap-2 pt-1">
                        <Input
                          value={crewDraft[team.id] || ''}
                          onChange={(e) => setCrewDraft((prev) => ({ ...prev, [team.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') addCrew(team); }}
                          placeholder="New crew name…"
                          className="h-8 text-sm bg-white max-w-xs"
                          data-testid={`add-crew-input-${team.id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs"
                          onClick={() => addCrew(team)}
                          data-testid={`add-crew-btn-${team.id}`}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add Crew
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Team create/edit dialog */}
      <Dialog open={!!teamDialog} onOpenChange={(open) => { if (!open) setTeamDialog(null); }}>
        <DialogContent data-testid="team-dialog">
          <DialogTitle>{teamDialog?.id ? 'Edit Team' : 'New Team'}</DialogTitle>
          <DialogDescription>
            A team is a container for crews; crews hold the members you assign during incidents and drills.
          </DialogDescription>
          <div className="space-y-3 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1">Team Name <span className="text-red-500">*</span></label>
              <Input
                autoFocus
                value={teamDialog?.team_name || ''}
                onChange={(e) => setTeamDialog((prev) => ({ ...prev, team_name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') saveTeam(); }}
                placeholder="e.g. Fire Response Team"
                data-testid="team-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={teamDialog?.description || ''}
                onChange={(e) => setTeamDialog((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description…"
                rows={3}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTeamDialog(null)}>Cancel</Button>
            <Button onClick={saveTeam} disabled={savingTeam} data-testid="team-save-btn">
              {savingTeam ? 'Saving…' : teamDialog?.id ? 'Save Changes' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crew members modal — same one used from the incident Participants widget */}
      {crewStaffTarget && (
        <CrewStaffModal
          crew={crewStaffTarget}
          isAdmin
          onClose={() => { setCrewStaffTarget(null); load(); }}
        />
      )}
      {teamStaffTarget && (
        <TeamStaffModal
          team={teamStaffTarget}
          isAdmin
          onClose={() => { setTeamStaffTarget(null); load(); }}
        />
      )}
    </div>
  );
};

export default TeamsAndCrews;
